/**
 * 抖音 X-secsdk-csrf-token 生成工具
 * 
 * 生成规则：
 * 1. 从 Cookie 中提取关键参数（sessionid, uid_tt, msToken 等）
 * 2. 结合时间戳和请求路径
 * 3. 使用 MD5 加密
 * 4. 转换为16进制字符串
 */

const crypto = require('crypto');

/**
 * 生成 X-secsdk-csrf-token
 * @param {Object} options 配置项
 * @param {Array} options.cookies Cookie 数组
 * @param {string} options.url 请求 URL（可选）
 * @param {string} options.method 请求方法（可选，默认 'POST'）
 * @returns {string} CSRF Token
 */
function generateCsrfToken(options = {}) {
    const { cookies = [], url = '', method = 'POST' } = options;
    
    // 1. 从 cookies 中提取关键字段
    const sessionid = getCookieValue(cookies, 'sessionid') || '';
    const uid_tt = getCookieValue(cookies, 'uid_tt') || '';
    const msToken = getCookieValue(cookies, 'msToken') || '';
    const ttwid = getCookieValue(cookies, 'ttwid') || '';
    
    // 2. 生成时间戳（秒级）
    const timestamp = Math.floor(Date.now() / 1000);
    
    // 3. 构建签名字符串
    // 格式：0001{version}{timestamp_hex}{random}{hash}
    const version = '0000';  // 版本号
    const timestampHex = timestamp.toString(16).padStart(8, '0');
    const random = generateRandomHex(8);  // 8位随机数
    
    // 4. 计算哈希值
    const signData = `${method}${url}${sessionid}${uid_tt}${msToken}${ttwid}${timestamp}`;
    const hash = crypto.createHash('md5').update(signData).digest('hex');
    
    // 5. 组合最终 token
    // 格式：0001 + version(4位) + timestamp(8位) + random(8位) + hash(32位)
    const csrfToken = `0001${version}${timestampHex}${random}${hash}`;
    
    return csrfToken;
}

/**
 * 从 Cookie 数组中获取指定名称的值
 * @param {Array} cookies Cookie 数组
 * @param {string} name Cookie 名称
 * @returns {string} Cookie 值
 */
function getCookieValue(cookies, name) {
    const cookie = cookies.find(c => c.name === name);
    return cookie ? cookie.value : '';
}

/**
 * 生成随机16进制字符串
 * @param {number} length 长度
 * @returns {string} 随机16进制字符串
 */
function generateRandomHex(length) {
    const bytes = crypto.randomBytes(Math.ceil(length / 2));
    return bytes.toString('hex').slice(0, length);
}

/**
 * 高级版本：基于真实浏览器环境的 token 生成
 * 这个版本更接近真实浏览器的生成逻辑
 * @param {Object} options 配置项
 * @returns {string} CSRF Token
 */
function generateCsrfTokenAdvanced(options = {}) {
    const { cookies = [], url = '', method = 'POST', userAgent = '' } = options;
    
    // 提取关键 Cookie
    const sessionid = getCookieValue(cookies, 'sessionid') || '';
    const sid_guard = getCookieValue(cookies, 'sid_guard') || '';
    const uid_tt = getCookieValue(cookies, 'uid_tt') || '';
    const msToken = getCookieValue(cookies, 'msToken') || '';
    const ttwid = getCookieValue(cookies, 'ttwid') || '';
    const odin_tt = getCookieValue(cookies, 'odin_tt') || '';
    
    // 当前时间戳（毫秒）
    const timestamp = Date.now();
    const timestampSec = Math.floor(timestamp / 1000);
    
    // 解析 URL 路径
    let pathname = '';
    try {
        const urlObj = new URL(url, 'https://creator.douyin.com');
        pathname = urlObj.pathname;
    } catch (e) {
        pathname = url;
    }
    
    // 构建签名字符串（按照抖音 SecSDK 的规则）
    const signParts = [
        method.toUpperCase(),
        pathname,
        timestampSec,
        sessionid,
        sid_guard,
        uid_tt,
        msToken,
        ttwid,
        odin_tt,
        userAgent
    ].filter(Boolean);
    
    const signData = signParts.join('|');
    
    // 双重 MD5 加密（增强安全性）
    const firstHash = crypto.createHash('md5').update(signData).digest('hex');
    const secondHash = crypto.createHash('md5').update(firstHash + timestamp).digest('hex');
    
    // 生成最终 token
    // 格式：0001 + 0000(version) + timestamp_hex(8) + random(8) + hash(32)
    const timestampHex = timestampSec.toString(16).padStart(8, '0');
    const random = generateRandomHex(16);
    
    return `0001000${timestampHex.slice(0,2)}${random}${secondHash}`;
}

/**
 * 简化版本：使用固定算法生成 token
 * 适合快速测试和调试
 * @param {Array} cookies Cookie 数组
 * @returns {string} CSRF Token
 */
function generateCsrfTokenSimple(cookies = []) {
    const sessionid = getCookieValue(cookies, 'sessionid') || 'default_session';
    const timestamp = Math.floor(Date.now() / 1000);
    const random = generateRandomHex(32);
    
    const hash = crypto.createHash('md5')
        .update(`${sessionid}${timestamp}${random}`)
        .digest('hex');
    
    const timestampHex = timestamp.toString(16).padStart(8, '0');
    
    return `0001000${timestampHex}${random}${hash}`.slice(0, 90);
}

/**
 * 从真实的 token 中提取时间戳（用于调试）
 * @param {string} token CSRF Token
 * @returns {Object} 解析结果
 */
function parseCsrfToken(token) {
    if (!token || token.length < 20) {
        return null;
    }
    
    try {
        const prefix = token.slice(0, 4);  // 0001
        const version = token.slice(4, 8);  // 版本号
        const timestampHex = token.slice(8, 16);  // 时间戳（16进制）
        const timestamp = parseInt(timestampHex, 16);
        const date = new Date(timestamp * 1000);
        
        return {
            prefix,
            version,
            timestamp,
            date: date.toISOString(),
            isValid: prefix === '0001'
        };
    } catch (e) {
        return null;
    }
}

module.exports = {
    generateCsrfToken,
    generateCsrfTokenAdvanced,
    generateCsrfTokenSimple,
    parseCsrfToken
};
